import {
  Box,
  Button,
  Flex,
  Image,
  Spinner,
  Text,
  VStack,
  useToken,
} from "@chakra-ui/react";
import axios from "axios";
import { t } from "i18next";
import { CloseIcon } from "@chakra-ui/icons";
import { memo, useEffect, useRef, useState } from "react";
import { GradientDivider } from "./componentsV2/GradientDivider";
import { FunnelComponentProps, FunnelContextArea } from "../funnel/types";
import { FUNNEL_TYPES } from "../funnel/utils/contants";
import { EventTypes } from "../../screens/root/api/eventsApi";
import { useIsVisible } from "../../hooks/useIsVisible";
import { useUserData } from "../../hooks/useUserData";
import { usePromoCode } from "./hooks/usePromoCode";
import { useSubscriptionPricingV2 } from "./hooks/useSubscriptionPricingV2";
import { FeatureValueProps } from "./componentsV2/FeatureValueProps";
import { SubscriptionNote } from "./componentsV2/SubscriptionNote";
import { ValueProps } from "./componentsV2/ValueProps";
import { Testimonials } from "./componentsV2/Testimonials";
import { SubscriptionSelector } from "./componentsV2/SubscriptionSelector";
import { StickyPurchaseButton } from "./componentsV2/StickyPurchaseButton";
import { useSubscriptionPurchaseV2 } from "./hooks/useSubscriptionPurchaseV2";
import { PromoCodeInput } from "./components/PromoCodeInput";
import { Eyebrow } from "./componentsV2/Eyebrow";
import { CaretLeftLargeIcon } from "../icons/CaretLeftLargeIcon";
import { interpretColorTags } from "../../utils/processPaywallText";
import { FunnelSteps } from "../funnel/FunnelSteps";
import { usePaywallType } from "../../hooks/usePaywallType";
import { useStripeCustomerPortal } from "./hooks/useStripeCustomerPortal";
import {
  useStripeCheckoutReturn,
  useStripeCheckoutReturnPollingTimeoutToast,
} from "./hooks/useStripeCheckoutReturn";
import {
  hasManageableWebSubscription,
  isLegacySubscriptionPageFlow,
  isLegacyWebSubscription,
  isStripeBillingManageableSubscription,
  redirectToLegacyWebSubscriptions,
} from "./api/paymentApi";
import { GET_ACCOUNT_ME_QUERY_KEY } from "../auth/api/authApi";
import { ACCOUNT_INIT_QUERY_KEY } from "../../appStore";
import { useRefetchQueries } from "../../services/axiosInstance";
import { HighlightValueProps } from "./componentsV2/HighlightValueProps";
import { RedesignedValueProps } from "./componentsV2/RedesignedValueProps";
import { BundleCard } from "./components/BundleCard";
import {
  SUBSCRIPTION_SETUP_PAYMENT_FORM_ID,
  SubscriptionSetupPayment,
} from "./components/SubscriptionSetupPayment";
import { useIsSubscriptionStripePaymentSettling } from "./utils/stripePaymentSettlingStore";
import { getPendingGuestStripeSubscriptionSetup } from "./utils/pendingGuestStripeSubscriptionSetup";

export const SubscriptionModalV2 = memo(
  ({
    onClose,
    modalContext = FunnelContextArea.default,
    onSuccess: onFunnelSuccess,
    sendEvent,
    setFunnelType,
    release,
    shouldShowSteps,
    stepCount,
    currentStep,
    stepText,
    stepDisplayText,
    showStepIndicators,
    initialPromoCode,
    triggerFocusPayment: _triggerFocusPayment = 0,
    hidePromoCode = false,
    hideValueProps = false,
    hideTitle = false,
    isPaywallHeader = false,
    customTitle,
    isInlineEmbed = false,
  }: FunnelComponentProps & {
    initialPromoCode?: string;
    triggerFocusPayment?: number;
    hidePromoCode?: boolean;
    hideValueProps?: boolean;
    hideTitle?: boolean;
    isPaywallHeader?: boolean;
    customTitle?: string;
    isInlineEmbed?: boolean;
  }) => {
    const { userData } = useUserData();
    const [blazeColor] = useToken("colors", ["blaze.blaze"]);

    const isGoldUser = userData?.gold ?? false;
    const isContextWithNoX = modalContext === FunnelContextArea.releaseDetails;
    const isContextWithPrePaywall =
      modalContext === FunnelContextArea.reader ||
      modalContext === FunnelContextArea.verticalReader;
    const paymentType = usePaywallType(release);
    const hasBackButton =
      isContextWithPrePaywall &&
      !isGoldUser &&
      paymentType === FUNNEL_TYPES.prePaywall;

    const {
      promoCode,
      appliedPromoCode,
      isPromoValid,
      promoValidationError,
      setPromoCode,
      setAppliedPromoCode,
    } = usePromoCode(initialPromoCode);

    const { yearlySub, monthlySub, activeType, setActiveType, paywall, data } =
      useSubscriptionPricingV2(appliedPromoCode);
    const [selectedWelcomeGiftId, setSelectedWelcomeGiftId] =
      useState<number>();
    const [showBundleSelection, setShowBundleSelection] = useState(false);
    const [pendingLegacyRedirectAfterAuth, setPendingLegacyRedirectAfterAuth] =
      useState(false);
    // Mirrors the embedded Stripe form's `submitting` flag. Without this,
    // the external Subscribe CTA stays idle for the full Stripe confirm
    // round-trip (~2-3s) — the existing `waiting` flag only flips during
    // SetupIntent creation, and `isSubscriptionSettling` doesn't flip
    // until after Stripe resolves. Tracking it locally lets the button
    // show a spinner immediately on click.
    const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);

    const {
      submitPurchase,
      waiting: checkoutWaiting,
      checkoutError,
      guestSetupIntent,
      completeGuestSetup,
    } = useSubscriptionPurchaseV2({
      activeType,
      yearlySub,
      monthlySub,
      promoCode,
      setFunnelType,
      welcomeGiftId: selectedWelcomeGiftId,
      hasSavedPaymentMethod:
        data?.data?.payload?.results?.provider?.has_saved_payment_method ===
        true,
      onFunnelSuccess,
      onPurchaseError: () => setShowBundleSelection(false),
    });
    const {
      openPortal,
      waiting: portalWaiting,
      error: portalError,
      setError: setPortalError,
    } = useStripeCustomerPortal();
    const refetchAfterStripeReturnAccount = useRefetchQueries(
      ACCOUNT_INIT_QUERY_KEY,
      GET_ACCOUNT_ME_QUERY_KEY,
    );

    const ref = useRef<HTMLDivElement>(null);
    const isVisible = useIsVisible(ref, 0.5);
    const alreadyTracked = useRef(false);
    const isSubscriptionSettling = useIsSubscriptionStripePaymentSettling();

    useEffect(() => {
      if (sendEvent && isVisible && !alreadyTracked.current) {
        sendEvent(EventTypes.viewed_subscription_offer);
        alreadyTracked.current = true;
      }
    }, [isVisible, sendEvent]);

    useEffect(() => {
      if (activeType !== "yearly") {
        setShowBundleSelection(false);
        // The welcome gift is a yearly-only entitlement; the backend rejects
        // monthly submissions that carry a welcome_gift_id (GX-3877). Clear
        // it whenever we leave yearly so a back→switch dance can't leak a
        // stale yearly gift id into a monthly submit.
        setSelectedWelcomeGiftId(undefined);
      }
    }, [activeType]);


    useEffect(() => {
      if (!pendingLegacyRedirectAfterAuth || !userData?.edit_url) {
        return;
      }

      setPendingLegacyRedirectAfterAuth(false);
      redirectToLegacyWebSubscriptions(userData.edit_url);
    }, [pendingLegacyRedirectAfterAuth, userData?.edit_url]);

    const mainTitle =
      customTitle || interpretColorTags(paywall?.main_title ?? "", blazeColor);
    const onPollingTimeout = useStripeCheckoutReturnPollingTimeoutToast();

    useStripeCheckoutReturn({
      expectedFlow: "portal",
      onSuccessReturn: async () => {
        await refetchAfterStripeReturnAccount();
      },
      onPollingTimeout,
    });

    const autoStartActiveSubOffer =
      yearlySub && monthlySub
        ? activeType === yearlySub.billing_frequency
          ? yearlySub
          : monthlySub
        : undefined;
    const autoStartProvider = data?.data.payload.results.provider;
    const autoStartUsesLegacySubscriptionPageFlow = autoStartProvider
      ? isLegacySubscriptionPageFlow(autoStartProvider)
      : false;
    const autoStartHasManagedSubscription = autoStartProvider
      ? hasManageableWebSubscription(autoStartProvider)
      : false;
    const autoStartHasSavedPaymentMethod =
      autoStartProvider?.has_saved_payment_method === true;
    const autoStartShouldShowBundleSelection =
      !!yearlySub?.welcome_gifts?.length && activeType === "yearly";
    const hasCompletedPendingGuestSetup =
      !!getPendingGuestStripeSubscriptionSetup()?.completedSetupIntentId;

    useEffect(() => {
      if (
        !data ||
        !yearlySub ||
        !monthlySub ||
        !paywall ||
        guestSetupIntent ||
        hasCompletedPendingGuestSetup ||
        checkoutWaiting ||
        autoStartUsesLegacySubscriptionPageFlow ||
        autoStartHasManagedSubscription ||
        autoStartHasSavedPaymentMethod ||
        autoStartShouldShowBundleSelection ||
        !isPromoValid ||
        !autoStartActiveSubOffer ||
        autoStartActiveSubOffer.access_token_type_id <= 0
      ) {
        return;
      }

      void submitPurchase();
    }, [
      autoStartActiveSubOffer,
      autoStartHasManagedSubscription,
      autoStartHasSavedPaymentMethod,
      autoStartShouldShowBundleSelection,
      autoStartUsesLegacySubscriptionPageFlow,
      checkoutWaiting,
      data,
      guestSetupIntent,
      hasCompletedPendingGuestSetup,
      isPromoValid,
      monthlySub,
      paywall,
      submitPurchase,
      yearlySub,
    ]);

    // Pre-create the SetupIntent the moment the user enters bundle-selection
    // (with the default first gift selected by handleShowBundleSelection)
    // so the embedded Stripe form is already mounted by the time they finish
    // looking at the gift cards. Without this, users had to click Subscribe
    // first just to reveal the form, then click again to submit it.
    // Gift changes after this point are safe — the finalize call sends the
    // current welcomeGiftId in its payload, which the backend prefers over
    // the SetupIntent metadata when stamping the subscription.
    // Pre-create the SetupIntent as soon as the modal has enough state to
    // generate a payload, so the embedded Stripe form is mounted and warm
    // on the first frame — no waiting for the bundle-step click. The
    // welcome_gift_id is applied at confirm time (createWebSubscriptionFromSetupSession)
    // rather than at SetupIntent creation, so it's safe to create before
    // the user picks a gift.
    useEffect(() => {
      if (
        guestSetupIntent ||
        hasCompletedPendingGuestSetup ||
        checkoutWaiting ||
        !data ||
        !yearlySub ||
        autoStartUsesLegacySubscriptionPageFlow ||
        autoStartHasManagedSubscription ||
        autoStartHasSavedPaymentMethod ||
        !isPromoValid
      ) {
        return;
      }

      void submitPurchase();
    }, [
      autoStartHasManagedSubscription,
      autoStartHasSavedPaymentMethod,
      autoStartUsesLegacySubscriptionPageFlow,
      checkoutWaiting,
      data,
      guestSetupIntent,
      hasCompletedPendingGuestSetup,
      isPromoValid,
      selectedWelcomeGiftId,
      showBundleSelection,
      submitPurchase,
      yearlySub,
    ]);

    if (!data || !yearlySub || !monthlySub || !paywall) {
      return (
        <Flex
          width="100%"
          justifyContent="center"
          alignItems="center"
          height="531px"
          padding={isInlineEmbed ? "0" : "24px"}
          ref={ref}
        >
          <Spinner color="blaze.blaze" />
        </Flex>
      );
    }

    const activeSubOffer =
      activeType === yearlySub.billing_frequency ? yearlySub : monthlySub;
    const provider = data.data.payload.results.provider;
    const usesLegacySubscriptionPageFlow =
      isLegacySubscriptionPageFlow(provider);
    const hasManagedSubscription = hasManageableWebSubscription(provider);
    const hasStripeBillingManagedSubscription =
      isStripeBillingManageableSubscription(provider);
    const hasLegacyManagedSubscription = isLegacyWebSubscription(provider);
    const waiting = checkoutWaiting || portalWaiting;
    const responseError = portalError ?? checkoutError ?? promoValidationError;
    const hasHighlightValueProps = !!paywall.highlight_value_props?.length;
    const hasWelcomeGifts = !!yearlySub.welcome_gifts?.length;
    const shouldShowBundleSelection =
      hasWelcomeGifts && activeType === "yearly";
    const bundleConfig = paywall.bundle_selection;
    const isGuestSetupPaymentVisible = !!guestSetupIntent;
    // When bundle-selection is still ahead, the CTA must route through
    // handleCtaClick to transition into the bundle step. Direct form
    // submission is only safe once we're past that gate.
    const shouldSubmitFormDirectly =
      isGuestSetupPaymentVisible &&
      (showBundleSelection || !shouldShowBundleSelection);

    const ctaText = hasManagedSubscription
      ? t("components.payments.subscription.manageSubscription")
      : showBundleSelection
        ? bundleConfig?.cta_text ||
          t("components.payments.bundleSelection.subscribe")
        : shouldShowBundleSelection && paywall.yearly_bundle
          ? paywall.yearly_bundle.cta_text
          : activeSubOffer.cta_text;

    const getResponseErrorMessage = (error: Error) => {
      let errorMessage = t(
        "components.payments.subscription.subscriptionDeclined",
      );

      if (axios.isAxiosError<{ payload?: { results?: string } }>(error)) {
        errorMessage = error.response?.data?.payload?.results || errorMessage;
      }

      return errorMessage;
    };

    const handleManagedSubscriptionClick = () => {
      setPortalError(undefined);
      if (usesLegacySubscriptionPageFlow && userData?.edit_url) {
        redirectToLegacyWebSubscriptions(userData.edit_url);
        return;
      }

      if (hasStripeBillingManagedSubscription) {
        void openPortal("manage").catch((portalOpenError) => {
          setPortalError(getResponseErrorMessage(portalOpenError as Error));
        });
        return;
      }

      if (hasLegacyManagedSubscription && userData?.edit_url) {
        redirectToLegacyWebSubscriptions(userData.edit_url);
      }
    };

    const handleShowBundleSelection = () => {
      if (!selectedWelcomeGiftId && yearlySub.welcome_gifts?.length) {
        setSelectedWelcomeGiftId(yearlySub.welcome_gifts[0].id);
      }
      setShowBundleSelection(true);
    };

    const handleCtaClick = () => {
      if (usesLegacySubscriptionPageFlow) {
        if (userData?.edit_url) {
          redirectToLegacyWebSubscriptions(userData.edit_url);
        } else {
          setPendingLegacyRedirectAfterAuth(true);
          setFunnelType(FUNNEL_TYPES.signup);
        }
        return;
      }

      if (hasManagedSubscription) {
        handleManagedSubscriptionClick();
        return;
      }

      if (!showBundleSelection && shouldShowBundleSelection) {
        handleShowBundleSelection();
        return;
      }

      void submitPurchase();
    };

    return (
      <Box ref={ref} position="relative" width="100%">
        <Box display={showBundleSelection ? "none" : undefined}>
          {paywall.background_image && (
            <Image
              src={paywall.background_image}
              position="absolute"
              top={0}
              left={0}
              width="100%"
              pointerEvents="none"
              zIndex={0}
              sx={{
                maskImage: {
                  base: "linear-gradient(to bottom, black 8%, transparent 60%)",
                  md: "linear-gradient(to bottom, black 10%, transparent 80%)",
                },
                WebkitMaskImage: {
                  base: "linear-gradient(to bottom, black 8%, transparent 60%)",
                  md: "linear-gradient(to bottom, black 10%, transparent 80%)",
                },
              }}
            />
          )}
          {!isInlineEmbed && (
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              padding="24px 24px 0"
              position="sticky"
              top={0}
              zIndex={2}
            >
              <Box>
                {hasBackButton && (
                  <Button
                    variant="iconOnly"
                    onClick={() => setFunnelType(FUNNEL_TYPES.prePaywall)}
                    minWidth={0}
                    backdropFilter="blur(12px)"
                    background="transparent.black.40"
                    borderRadius="full"
                    boxSize="40px"
                  >
                    <CaretLeftLargeIcon color="dune.400" boxSize="20px" />
                  </Button>
                )}
              </Box>
              {isContextWithNoX ? null : (
                <Button
                  gc-testing-id="btnCloseSubscriptionModal"
                  variant="iconOnly"
                  onClick={onClose}
                  minWidth={0}
                  backdropFilter="blur(12px)"
                  background="transparent.black.40"
                  borderRadius="full"
                  boxSize="40px"
                >
                  <CloseIcon color="dune.100" />
                </Button>
              )}
            </Flex>
          )}
          <VStack
            gap={6}
            py={isInlineEmbed ? 0 : 6}
            alignItems="center"
            width="100%"
            position="relative"
            zIndex={1}
          >
            {!hideTitle && (
              <VStack
                gap={6}
                px={isInlineEmbed ? 0 : 6}
                alignItems="center"
                width="100%"
              >
                <VStack alignItems="center" width="100%" mb={2}>
                  <Eyebrow
                    text={paywall.eyebrow}
                    imageUrl={paywall.eyebrow_image_url}
                  />
                  {shouldShowSteps ? (
                    <FunnelSteps
                      stepCount={stepCount}
                      currentStep={currentStep}
                      stepText={stepText}
                      text={stepDisplayText}
                      hideProgressIndicators={!showStepIndicators}
                    />
                  ) : (
                    <Text
                      mt="16px"
                      variant="paymentTitle"
                      {...(isPaywallHeader && {
                        fontSize: { base: "22px", md: "32px" },
                        fontWeight: "600",
                      })}
                      dangerouslySetInnerHTML={{ __html: mainTitle }}
                    />
                  )}
                </VStack>
              </VStack>
            )}
            <VStack
              gap={6}
              px={isInlineEmbed ? 0 : 6}
              alignItems="center"
              width="100%"
            >
              {!hideValueProps && (
                <FeatureValueProps valueProps={paywall.feature_value_props} />
              )}
              <SubscriptionSelector
                subOffer={yearlySub}
                isActive={activeType === yearlySub.billing_frequency}
                onClick={() => setActiveType(yearlySub.billing_frequency)}
                creatorLogos={
                  hasHighlightValueProps || paywall.yearly_bundle
                    ? undefined
                    : paywall.creator_logos
                }
                creatorsCount={
                  hasHighlightValueProps ||
                  paywall.yearly_bundle ||
                  !paywall.creator_logos
                    ? undefined
                    : paywall.creators_count
                }
                yearlyBundle={paywall.yearly_bundle}
              />
              <SubscriptionSelector
                subOffer={monthlySub}
                isActive={activeType === monthlySub.billing_frequency}
                onClick={() => setActiveType(monthlySub.billing_frequency)}
                creatorLogos={paywall.creator_logos}
                creatorsCount={
                  paywall.creator_logos ? paywall.creators_count : undefined
                }
              />
              <GradientDivider />
              {/* The Subscribe CTA lives in <StickyPurchaseButton/> below
                  (portal'd to the modal's bottom sheet). No inline button
                  here — rendering one inside the scroll area too produced
                  a duplicate "Pick a bundle and subscribe" CTA visible at
                  the same time as the sticky one. */}
              {responseError ? (
                <Text
                  color="error.300"
                  fontSize="14px"
                  lineHeight="130%"
                  width="100%"
                >
                  {responseError}
                </Text>
              ) : null}
              {hidePromoCode ? null : (
                <PromoCodeInput
                  promoApplied={appliedPromoCode}
                  isValid={
                    appliedPromoCode === yearlySub?.promo_code ||
                    appliedPromoCode === monthlySub?.promo_code
                  }
                  applyPromoCode={setAppliedPromoCode}
                  onChange={setPromoCode}
                />
              )}
              <GradientDivider />
              <SubscriptionNote subscriptionNote={paywall.subscription_note} />
            </VStack>
            <Testimonials testimonials={paywall.testimonials} />
            {!hideValueProps &&
              (hasHighlightValueProps ? (
                <>
                  <HighlightValueProps
                    valueProps={paywall.highlight_value_props!}
                  />
                  <RedesignedValueProps valueProps={paywall.value_props} />
                </>
              ) : (
                <ValueProps
                  vpTitle={paywall.vp_title}
                  valueProps={paywall.value_props}
                />
              ))}
          </VStack>
        </Box>
        {showBundleSelection && yearlySub.welcome_gifts && (
          <>
            {!isInlineEmbed && (
              <Flex
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                padding="24px 24px 0"
                position="sticky"
                top={0}
                zIndex={2}
              >
                <Button
                  gc-testing-id="btnBackFromBundleSelection"
                  variant="iconOnly"
                  onClick={() => setShowBundleSelection(false)}
                  minWidth={0}
                  backdropFilter="blur(12px)"
                  background="transparent.black.40"
                  borderRadius="full"
                  boxSize="40px"
                >
                  <CaretLeftLargeIcon color="dune.400" boxSize="20px" />
                </Button>
                {isContextWithNoX ? null : (
                  <Button
                    gc-testing-id="btnCloseSubscriptionModal"
                    variant="iconOnly"
                    onClick={onClose}
                    minWidth={0}
                    backdropFilter="blur(12px)"
                    background="transparent.black.40"
                    borderRadius="full"
                    boxSize="40px"
                  >
                    <CloseIcon color="dune.100" />
                  </Button>
                )}
              </Flex>
            )}
            <VStack gap={6} px={4} py={8} alignItems="stretch" width="100%">
              <VStack gap={2} alignItems="center" letterSpacing="-0.5px">
                <Text
                  fontWeight={700}
                  fontSize="24px"
                  lineHeight="1.3"
                  color="white"
                  textAlign="center"
                >
                  {bundleConfig?.title ||
                    t("components.payments.bundleSelection.title")}
                </Text>
                <Text
                  fontSize="16px"
                  lineHeight="1.3"
                  color="neutral.400"
                  textAlign="center"
                >
                  {bundleConfig?.subtitle ||
                    t("components.payments.bundleSelection.subtitle")}
                </Text>
              </VStack>
              <VStack gap={4} width="100%">
                {yearlySub.welcome_gifts.map((gift) => (
                  <BundleCard
                    key={gift.id}
                    gift={gift}
                    isSelected={selectedWelcomeGiftId === gift.id}
                    onSelect={() => setSelectedWelcomeGiftId(gift.id)}
                  />
                ))}
              </VStack>
            </VStack>
          </>
        )}
        <StickyPurchaseButton
          isDisabled={
            waiting ||
            isSubscriptionSettling ||
            isConfirmingPayment ||
            (!isPromoValid && !hasManagedSubscription)
          }
          loading={waiting || isSubscriptionSettling || isConfirmingPayment}
          text={isSubscriptionSettling ? "Confirming your purchase…" : ctaText}
          // Hand off to form-submit ONLY when there's no further step to
          // route through. While bundle-selection is still ahead we must
          // call handleCtaClick so it can transition us into the bundle
          // step — otherwise the CTA fires `form.requestSubmit()` and
          // skips the bundle picker entirely.
          onClick={shouldSubmitFormDirectly ? undefined : handleCtaClick}
          formId={
            shouldSubmitFormDirectly
              ? SUBSCRIPTION_SETUP_PAYMENT_FORM_ID
              : undefined
          }
          isInlineEmbed={isInlineEmbed}
        >
          {isGuestSetupPaymentVisible && !isSubscriptionSettling && (
            // Mount the form once and keep it mounted for the lifetime
            // of the SetupIntent — toggle visibility via `display: none`
            // instead of conditional rendering. Stripe Elements is heavy
            // to re-initialize (iframe load, Link auth round-trip,
            // ExpressCheckout availability check) and unmount/remount
            // produces a visible reload every time the user switches
            // tier or bounces through the bundle step. The finalize
            // endpoint accepts any subscription `accessTokenTypeId`
            // against the same SetupIntent, so the iframe doesn't need
            // to be re-issued when the user toggles yearly ↔ monthly.
            //
            // Visibility rules:
            // - shouldSubmitFormDirectly false → user hasn't entered
            //   the bundle step yet; hide so Link/Apple/Google Pay
            //   can't bypass the gift picker (GX-3878).
            // - isConfirmingPayment true → a confirm round-trip is in
            //   flight; hide so a second click can't fire a parallel
            //   confirmSetup() while the first is still resolving.
            <Box
              width="100%"
              display={
                shouldSubmitFormDirectly && !isConfirmingPayment
                  ? undefined
                  : "none"
              }
            >
              <SubscriptionSetupPayment
                clientSecret={guestSetupIntent.client_secret}
                setupIntentId={guestSetupIntent.setup_intent_id}
                clientNonce={guestSetupIntent.client_nonce}
                submitButtonText={ctaText}
                disabled={
                  waiting || (!isPromoValid && !hasManagedSubscription)
                }
                onComplete={completeGuestSetup}
                hideInternalSubmitButton
                onSubmittingChange={setIsConfirmingPayment}
              />
            </Box>
          )}
        </StickyPurchaseButton>
      </Box>
    );
  },
);
